/**
 * orthogonalize — redresse un polygone / polyligne : les côtés proches (± tolérance) de l'orientation dominante ou de sa perpendiculaire
 * sont alignés dessus, les autres (pans coupés à 45°…) gardent leur angle ; les sommets = intersections des côtés redressés (sommets colinéaires fusionnés).
 * `scale` / `ratio` convertissent les coordonnées normalisées en mètres (fond anisotrope) pour mesurer de vrais angles.
 */
export const orthogonalize = (points, { scale = 1, ratio = 1, closed = true, toleranceDeg = 12 } = {}) => {
  if (!points || points.length < 3) return points;
  const M = points.map(([x, y]) => [x * scale, y * scale * ratio]);
  const n = M.length;
  const edges = [];
  for (let i = 0; i < (closed ? n : n - 1); i += 1) {
    const a = M[i], b = M[(i + 1) % n];
    const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
    edges.push({ a, b, ang, len: Math.hypot(b[0] - a[0], b[1] - a[1]), mid: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] });
  }
  // orientation dominante (angles ramenés modulo 90° via l'angle ×4, pondérés par la longueur)
  let sx = 0, sy = 0;
  edges.forEach((e) => { sx += Math.cos(4 * e.ang) * e.len; sy += Math.sin(4 * e.ang) * e.len; });
  const dom = Math.atan2(sy, sx) / 4;
  const tol = (toleranceDeg * Math.PI) / 180;
  const snapped = edges.map((e) => {
    let best = e.ang, bestD = Infinity;
    for (let k = 0; k < 4; k += 1) {
      const cand = dom + (k * Math.PI) / 2;
      let d = e.ang - cand; d = Math.atan2(Math.sin(d), Math.cos(d));
      if (Math.abs(d) < bestD) { bestD = Math.abs(d); best = cand; }
    }
    return { ...e, ang: bestD <= tol ? best : e.ang };
  });
  const intersect = (p, u, q, v) => {
    const det = u[0] * v[1] - u[1] * v[0];
    if (Math.abs(det) < 1e-6) return null;
    const t = ((q[0] - p[0]) * v[1] - (q[1] - p[1]) * v[0]) / det;
    return [p[0] + u[0] * t, p[1] + u[1] * t];
  };
  const dir = (e) => [Math.cos(e.ang), Math.sin(e.ang)];
  const out = [];
  const E = snapped.length;
  for (let i = 0; i < n; i += 1) {
    const prev = snapped[(i - 1 + E) % E], next = snapped[i % E];
    if (!closed && (i === 0 || i === n - 1)) {
      const e = i === 0 ? next : prev; const p = i === 0 ? M[0] : M[n - 1]; const d = dir(e);
      const t = (p[0] - e.mid[0]) * d[0] + (p[1] - e.mid[1]) * d[1];
      out.push([e.mid[0] + d[0] * t, e.mid[1] + d[1] * t]); continue;
    }
    const x = intersect(prev.mid, dir(prev), next.mid, dir(next));
    if (x) out.push(x); // côtés colinéaires → le sommet disparaît
  }
  if (out.length < (closed ? 3 : 2)) return points;
  return out.map(([x, y]) => [+(x / scale).toFixed(6), +(y / (scale * ratio)).toFixed(6)]);
};
